import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DownloadButton } from "../../components/Core/Button/Button";
import Dropdown from "../../components/Core/Dropdown/Dropdown";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import { ConsultantReportBreadcrumbs } from "../../configs/breadcrumbNavigation";
import {
	buildBranchesKeyValues,
	buildPoolKeyValues,
	statusTypes,
} from "../../configs/Reports.config";
import CertificationReportFilter, {
	CertificationFilterValue,
} from "../../components/Shared/CertificationReportFilter/CertificationReportFilter";
import { KeyValue } from "../../models/KeyValue";
import { useOrganisation } from "../../networking/organisation.hooks";
import { filterArchivedCompetences } from "../../utils/competence.utils";
import { apiStore } from "../../stores/api.store";
import { reportStore } from "../../networking/report.api";

const ConsultantsReportPage: React.FC = () => {
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();

	const [pool, setPool] = useState<KeyValue>();
	const [status, setStatus] = useState<KeyValue>();
	const [certification, setCertification] = useState<CertificationFilterValue>({});
	const [branch, setBranch] = useState<KeyValue>();
	const [service, setService] = useState<KeyValue | any>(null);
	const [services, setServices] = useState<Array<KeyValue>>([]);
	const [loading, setLoading] = useState<boolean>();
	const [branchMenuOpen, setBranchMenuOpen] = useState<boolean>();
	const selectInputRef: any = useRef<any>();

	const pools = buildPoolKeyValues(organisation?.pools || [], t("ALL"));

	const branches = buildBranchesKeyValues(
		filterArchivedCompetences(organisation?.competences || []),
		t("ALL")
	);

	const handlePoolChange = (e: KeyValue) => {
		setPool(e);
	};

	const handleStatusChange = (e: KeyValue) => {
		setStatus(e);
	};

	const handleBranchChange = (e: KeyValue) => {
		const tempBranch = branches.find((branch) => branch.key === e.key);
		setServices(tempBranch?.items || []);

		setBranch(e);
	};

	const handleServiceChange = (e: KeyValue) => {
		setService(e);
	};

	const onSubmit = async () => {
		let selectedPool!: string;
		let selectedStatus!: string;
		let selectedBranch!: string;
		let selectedService!: string;

		if (pool?.key && pool.key !== "") selectedPool = pool.key as string;
		if (status?.key && status.key !== "") selectedStatus = status.key as string;

		if (branch?.key && branch.key !== "") selectedBranch = branch.key as string;

		if (service?.key && service.key !== "") selectedService = service.key as string;

		setLoading(true);
		const url = await reportStore.getConsultantsReport(
			selectedPool,
			selectedStatus,
			selectedBranch,
			selectedService,
			certification
		);
		setLoading(false);

		if (url) {
			// https://stackoverflow.com/a/59352848
			const downloadLink = document.createElement("a");
			downloadLink.href = apiStore.apiRoot + url;
			downloadLink.download = apiStore.apiRoot + url;
			downloadLink.click();
		}
	};

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={ConsultantReportBreadcrumbs} />
			<Flex flexWrap="wrap" margin="1.5rem 0" maxWidth="900px">
				<Dropdown<KeyValue, KeyValue>
					value={status}
					getValue={(o) => o}
					onChange={handleStatusChange}
					getOptionLabel={(option) => option.value}
					options={statusTypes()}
					placeholder={"reports.filters.status.placeHolder"}
					label={"reports.filters.status.label"}
					width="300px"
					margin="0.75rem 0.5rem"
				/>
				{pools.length > 0 && (
					<Dropdown<KeyValue, KeyValue>
						value={pool}
						getValue={(o) => o}
						onChange={handlePoolChange}
						getOptionLabel={(option) => option.value}
						options={pools}
						placeholder={"reports.filters.pool.placeHolder"}
						label={"reports.filters.pool.label"}
						width="300px"
						margin="0.75rem 0.5rem"
					/>
				)}
				<CertificationReportFilter
					certificationTypes={organisation?.certificationTypes}
					value={certification}
					onChange={setCertification}
					allLabel={t("ALL")}
				/>
				<Dropdown<KeyValue, KeyValue>
					value={branch}
					getValue={(o) => o}
					onChange={handleBranchChange}
					getOptionLabel={(option) => option.value}
					options={branches}
					onMenuOpen={() => setBranchMenuOpen(true)}
					onMenuClose={() => setBranchMenuOpen(false)}
					placeholder={"reports.filters.branch.placeHolder"}
					label={"reports.filters.branch.label"}
					width="300px"
					margin="0.75rem 0.5rem"
				/>
				{!branchMenuOpen && (
					<Dropdown<KeyValue, KeyValue>
						inputRef={selectInputRef}
						value={service}
						getValue={(o) => o}
						onChange={handleServiceChange}
						getOptionLabel={(option) => option.value}
						options={services || []}
						placeholder={"reports.filters.service.placeHolder"}
						label={"reports.filters.service.label"}
						width="300px"
						margin="0.75rem 0.5rem"
					/>
				)}
			</Flex>
			<DownloadButton
				width="200px"
				height="38px"
				fontSize="m"
				margin="2rem 0.5rem"
				onClick={onSubmit}
				loading={loading}
			>
				{t("reports.export")}
			</DownloadButton>
		</div>
	);
};

export default ConsultantsReportPage;
